import images from '@assets/images/index'
import GroupDropDown from '@components/GroupDropDown'
import {
  AssessmentColumnType as ColumnType,
  useAssessmentTable
} from '@suntex.fim/assessment'
import { Button } from '@components/ui'
import { useQuery } from '@tanstack/react-query'
import { Breadcrumb, Flex, Spin, message } from 'antd'
import { useMemo, useState } from 'react'

import { Link, useNavigate } from '@suntex.core/nav'
import {
  Database_Tables_WDYKTemplate,
  Entities_Modules_Assign_Player
} from '@suntex.fim/api-client'
import { isAuthenticated, useAuth } from '@suntex.fim/auth/react'
import { useFramework } from '@suntex.fim/framework'
import { useTeamInfo } from '@suntex.fim/team'
import { LevelSelect, SearchInput } from '@suntex.fim/ui'

import { testColumns } from './AssessmentReport/AssessmentReportWDYKTable'

export const timeAllowedOptions = [
  {
    key: 1,
    label: '15 minutes'
  },
  {
    key: 2,
    label: '30 minutes'
  },
  {
    key: 4,
    label: '60 minutes'
  }
]

function AssessmentReportWDYK2Test() {
  const { api } = useFramework()
  const navigate = useNavigate()
  const { login } = useAuth(isAuthenticated)
  const team = useTeamInfo()
  const [searchQuery, setSearchQuery] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const memoColumns = useMemo<ColumnType<Entities_Modules_Assign_Player>[]>(
    () => testColumns() as ColumnType<Entities_Modules_Assign_Player>[],
    []
  )

  const [templateId, setTemplateId] = useState<number>()
  const [timeAllowed, setTimeAllowed] = useState<number | string>(1)

  const { data: queryData, isLoading } = useQuery({
    queryKey: ['assessment', 'wdykTestPlayers', team?.id],
    queryFn: async () => {
      const response = await api.get_Modules_SuggestAssign({
        teamId: team!.id
      })
      return {
        players: response.players || [],
        wdykTemplates: response.wdykTemplates || []
      }
    },
    enabled: team?.id != null
  })

  const testPlayer = queryData?.players ?? []
  const wdykTemplates = queryData?.wdykTemplates ?? []

  const { table, Table } = useAssessmentTable<Entities_Modules_Assign_Player>({
    data: testPlayer,
    columns: memoColumns,
    isLoading: isLoading || isSubmitting,
    globalFilter: searchQuery,
    initialSorting: [{ id: 'userId', desc: true }],
    displayPagination: false
  })

  const selectedRow = table.getSelectedRowModel().flatRows
  const isDisabled = !selectedRow.length
  const createTest = async () => {
    const playerIds: number[] = []
    selectedRow.forEach((player) => {
      playerIds.push(player.original.id || 0)
    })
    setIsSubmitting(true)
    try {
      const MINUTES_TO_SECONDS = 60
      const TIME_MULTIPLIER = 15
      await api.post_Modules_CreateWdyk({
        loginId: login.id,
        templateId: templateId!,
        timeAllowed: Number(timeAllowed) * TIME_MULTIPLIER * MINUTES_TO_SECONDS,
        playerIds
      })
      message.success(
        'Test is created and notification is sent to each player '
      )
      navigate(`/team/${team?.id}/assessment-report/what-do-you-know`)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <>
      {/* navigation */}
      <Flex vertical gap="20px">
        <Breadcrumb
          items={[
            {
              title: 'Team Assessment Reports-Goals Index'
            },
            {
              title: (
                <Link
                  to={`/team/${team?.id}/assessment-report/what-do-you-know`}
                >
                  <b>What Do You Know</b>
                </Link>
              )
            }
          ]}
        />
        <h1>Assign What Do You Know</h1>

        {/* result */}
        <div className="main-result">
          {isLoading ? (
            <Spin />
          ) : (
            <Flex gap={10} className="wdyk-dropdown" wrap>
              <GroupDropDown
                data={wdykTemplates}
                setTemplateId={setTemplateId}
              />
              <div className="report-filter wdyk-dropdown__select">
                <p className="dd-label fw-bold text-sm">Time:</p>
                <LevelSelect
                  value={timeAllowed}
                  onChange={(newLevel) => setTimeAllowed(newLevel)}
                  options={timeAllowedOptions}
                />
              </div>
              <Flex gap={10} align="end" className="wdyk-dropdown__searchbar">
                <div className="report-search">
                  <SearchInput value={searchQuery} onChange={setSearchQuery} />
                </div>
                <Button
                  shape="round"
                  intent="primary"
                  disabled={isDisabled || !templateId}
                  onClick={createTest}
                >
                  Create test <images.AddCircleWhite />
                </Button>
              </Flex>
            </Flex>
          )}
          <Table />
        </div>
      </Flex>
    </>
  )
}

export default AssessmentReportWDYK2Test
